import React from 'react';
import marbleTex from './backdrop-textures/marble-slab.webp';
import woodTex from './backdrop-textures/wood-surface.webp';
import linenTex from './backdrop-textures/linen-fabric.webp';
import concreteTex from './backdrop-textures/concrete.webp';
import plasterTex from './backdrop-textures/textured-plaster.webp';
import concreteSoftTex from './backdrop-textures/concrete-soft.webp';
import dappledMarbleTex from './backdrop-textures/dappled-marble.webp';
import waterTex from './backdrop-textures/water-surface.webp';

/**
 * Backdrop swatches — literal color/material samples rendered as small
 * textured cards. Users pick based on "what the surface would look like."
 * Material surfaces use a pre-generated seamless texture tile (bundled as a
 * tiny WebP); flat colours/gradients stay as CSS.
 */

const baseStyle: React.CSSProperties = {
    width: '100%',
    aspectRatio: '4/3',
    display: 'block',
    borderRadius: 6,
};

interface BackdropSwatchProps {
    slug: string;
}

export const BACKDROP_SWATCHES: Record<
    string,
    { gradient: string; image?: string; overlay?: React.ReactNode }
> = {
    'white-seamless': { gradient: 'linear-gradient(180deg, #FFFFFF 0%, #F8F8F8 100%)' },
    'off-white': { gradient: 'linear-gradient(180deg, #FAF6EE 0%, #F0E9D9 100%)' },
    'light-grey': { gradient: 'linear-gradient(180deg, #E8E8E8 0%, #D4D4D4 100%)' },
    'charcoal': { gradient: 'linear-gradient(180deg, #3A3A3A 0%, #1F1F1F 100%)' },
    'black-seamless': { gradient: 'linear-gradient(180deg, #1A1A1A 0%, #000000 100%)' },
    'warm-gradient': { gradient: 'linear-gradient(180deg, #FFE4C4 0%, #D97856 100%)' },
    'cool-gradient': { gradient: 'linear-gradient(180deg, #DFE9F5 0%, #7396B9 100%)' },
    // Editorial material moods (real texture tiles).
    'textured-plaster': {
        image: plasterTex,
        gradient: 'linear-gradient(180deg, #F3ECDD 0%, #E6DAC2 100%)',
    },
    'warm-paper': {
        // Soft warm-paper gradient (no texture needed).
        gradient: 'linear-gradient(160deg, #FBF4E6 0%, #F1E3C9 60%, #E7D4B2 100%)',
    },
    'concrete-soft': {
        image: concreteSoftTex,
        gradient: 'linear-gradient(180deg, #C6C1B8 0%, #9C978E 100%)',
    },
    'dappled-marble': {
        image: dappledMarbleTex,
        gradient: 'linear-gradient(180deg, #F6F3EE 0%, #E4DED4 100%)',
    },
    'water-surface': {
        image: waterTex,
        gradient: 'linear-gradient(180deg, #CFE1EC 0%, #9FBFD3 100%)',
    },
    'concrete': {
        image: concreteTex,
        // CSS fallback: base tone + faint mottling.
        gradient:
            'radial-gradient(circle at 30% 25%, rgba(255,255,255,0.18) 0%, transparent 40%),' +
            'radial-gradient(circle at 70% 75%, rgba(0,0,0,0.12) 0%, transparent 45%),' +
            'linear-gradient(180deg, #B8B3AC 0%, #8A857E 100%)',
    },
    'marble-slab': {
        image: marbleTex,
        // CSS fallback: cream base + soft diagonal veins.
        gradient:
            'linear-gradient(115deg, transparent 44%, rgba(120,120,130,0.28) 46%, transparent 48%),' +
            'linear-gradient(125deg, transparent 62%, rgba(120,120,130,0.18) 64%, transparent 67%),' +
            'linear-gradient(180deg, #F5F0E8 0%, #E8E0D0 100%)',
    },
    'wood-surface': {
        image: woodTex,
        // CSS fallback: brown base + fine grain lines.
        gradient:
            'repeating-linear-gradient(92deg, rgba(0,0,0,0.10) 0 1px, transparent 1px 6px),' +
            'linear-gradient(180deg, #C49460 0%, #8B5A3C 100%)',
    },
    'linen-fabric': {
        image: linenTex,
        // CSS fallback: woven crosshatch over a warm oat base.
        gradient:
            'repeating-linear-gradient(0deg, rgba(0,0,0,0.06) 0 1px, transparent 1px 4px),' +
            'repeating-linear-gradient(90deg, rgba(0,0,0,0.06) 0 1px, transparent 1px 4px),' +
            'linear-gradient(180deg, #E8DFC8 0%, #BEB194 100%)',
    },
};

export const BackdropSwatch: React.FC<BackdropSwatchProps> = ({ slug }) => {
    const swatch = BACKDROP_SWATCHES[slug] || BACKDROP_SWATCHES['white-seamless'];
    return (
        <div
            style={{
                ...baseStyle,
                ...(swatch.image
                    ? { backgroundImage: `url(${swatch.image})`, backgroundSize: 'cover', backgroundPosition: 'center' }
                    : { background: swatch.gradient }),
                border: '1px solid rgba(0,0,0,0.08)',
                boxShadow: 'inset 0 1px 2px rgba(255,255,255,0.2)',
            }}
        />
    );
};
